import { useState, useEffect, useCallback } from "react";
import { ChevronRight, ChevronLeft, X, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

const TOUR_STEPS = [
  {
    target: '[data-testid="new-chat-btn"]',
    title: "Start a Conversation",
    desc: "Tap here to begin a new chat. Pick a module like Friend or Mentor to set the tone.",
    position: "right",
  },
  {
    target: '[data-testid="quick-mood-welcome"]',
    title: "Quick Mood Check-in",
    desc: "Log how you're feeling with a single tap. This fuels your mood insights and daily recaps.",
    position: "top",
    fallback: true,
  },
  {
    target: '[data-testid="template-0"]',
    title: "Starter Templates",
    desc: "Not sure what to say? Pick a prompt to jump straight into a meaningful conversation.",
    position: "top",
    fallback: true,
  },
  {
    target: '[data-testid="folder-all"]',
    title: "Conversation Folders",
    desc: "Organize your chats into folders. You can create your own or let AI auto-categorize them.",
    position: "right",
  },
  {
    target: '[data-testid="mood-sparkline"]',
    title: "Mood Sparkline",
    desc: "A 7-day mood trend at a glance — see how your emotional state evolves across conversations.",
    position: "bottom",
    fallback: true,
  },
  {
    target: 'a[href="/bookmarks"]',
    title: "Bookmarks",
    desc: "Save any message by hovering and clicking the bookmark icon. Find them all here.",
    position: "right",
  },
];

export const FeatureTour = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const [visible, setVisible] = useState(true);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const positionTooltip = useCallback(() => {
    const s = TOUR_STEPS[step];
    const el = document.querySelector(s.target);
    if (el) {
      const r = el.getBoundingClientRect();
      setRect(r);
    } else if (s.fallback) {
      setRect(null);
    } else {
      if (step < TOUR_STEPS.length - 1) setStep(step + 1);
      else finish();
    }
  }, [step]);

  useEffect(() => {
    positionTooltip();
    window.addEventListener("resize", positionTooltip);
    return () => window.removeEventListener("resize", positionTooltip);
  }, [positionTooltip]);

  const finish = () => {
    if (dontShowAgain) {
      localStorage.setItem("omni_tour_done", "permanent");
    } else {
      localStorage.setItem("omni_tour_done", "true");
    }
    setVisible(false);
    onComplete?.();
  };

  const next = () => {
    if (step < TOUR_STEPS.length - 1) setStep(step + 1);
    else finish();
  };

  const prev = () => {
    if (step > 0) setStep(step - 1);
  };

  if (!visible) return null;

  const current = TOUR_STEPS[step];

  let tooltipStyle = {};
  let arrowClass = "";
  if (rect) {
    const pad = 16;
    if (current.position === "right") {
      tooltipStyle = { top: rect.top + rect.height / 2 - 60, left: rect.right + pad };
      arrowClass = "before:absolute before:-left-2 before:top-1/2 before:-translate-y-1/2 before:border-8 before:border-transparent before:border-r-[hsl(var(--card))]";
    } else if (current.position === "bottom") {
      tooltipStyle = { top: rect.bottom + pad, left: rect.left + rect.width / 2 - 160 };
      arrowClass = "before:absolute before:-top-2 before:left-1/2 before:-translate-x-1/2 before:border-8 before:border-transparent before:border-b-[hsl(var(--card))]";
    } else if (current.position === "top") {
      tooltipStyle = { top: rect.top - pad - 180, left: rect.left + rect.width / 2 - 160 };
      arrowClass = "before:absolute before:-bottom-2 before:left-1/2 before:-translate-x-1/2 before:border-8 before:border-transparent before:border-t-[hsl(var(--card))]";
    }
  }

  return (
    <div className="fixed inset-0 z-[60]" data-testid="feature-tour">
      <div className="absolute inset-0 bg-black/50 transition-all duration-300" onClick={finish} />

      {rect && (
        <div
          className="absolute rounded-2xl ring-4 ring-primary/40 bg-transparent z-[61] transition-all duration-300 pointer-events-none"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
          }}
        />
      )}

      <div
        data-testid="tour-tooltip"
        className={cn(
          "z-[62] w-80 bg-card border border-border/50 rounded-2xl shadow-2xl p-5 animate-in fade-in slide-in-from-bottom-2 duration-200",
          arrowClass,
          !rect && "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        )}
        style={rect ? { position: "fixed", ...tooltipStyle } : {}}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-xs text-muted-foreground font-medium">
              {step + 1} of {TOUR_STEPS.length}
            </span>
          </div>
          <button
            data-testid="tour-skip"
            onClick={finish}
            className="p-1 rounded-lg hover:bg-muted/50 text-muted-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <h3 className="font-heading text-base font-medium mb-1.5">{current.title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed mb-4">{current.desc}</p>

        {/* Don't show again checkbox */}
        <label
          data-testid="tour-dont-show"
          className="flex items-center gap-2 mb-4 cursor-pointer select-none"
        >
          <input
            type="checkbox"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            className="w-3.5 h-3.5 rounded border-border accent-primary cursor-pointer"
          />
          <span className="text-xs text-muted-foreground">Don't show again</span>
        </label>

        {/* Progress dots + controls */}
        <div className="flex items-center justify-between">
          <div className="flex gap-1.5">
            {TOUR_STEPS.map((_, i) => (
              <div
                key={i}
                className={cn(
                  "w-1.5 h-1.5 rounded-full transition-all",
                  i === step ? "bg-primary w-4" : i < step ? "bg-primary/40" : "bg-muted-foreground/20"
                )}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                data-testid="tour-prev"
                onClick={prev}
                className="p-1.5 rounded-xl hover:bg-muted/50 text-muted-foreground"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            <button
              data-testid="tour-next"
              onClick={next}
              className="px-4 py-1.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-all flex items-center gap-1"
            >
              {step === TOUR_STEPS.length - 1 ? "Got it!" : "Next"}
              {step < TOUR_STEPS.length - 1 && <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
